import { PasswordPolicyType } from "./types"

export const defaultMinimumPasswordLength = 8
const defaultPasswordString = "a number, lowercase letter, uppercase letter, and symbol"
type PasswordPolicy = {
    minimumLength: number
    temporaryPasswordValidityDays: number
    requires: {
        lowercase?: boolean
        numbers?: boolean
        symbols?: boolean
        uppercase?: boolean
    }
}

export const defaultPasswordPolicy = {
    minimumLength: defaultMinimumPasswordLength,
    temporaryPasswordValidityDays: 14,
    requires: {
        lowercase: true,
        numbers: true,
        symbols: true,
        uppercase: true,
    },
} satisfies PasswordPolicy

export function checkPasswordPolicy(
    password: string,
    passwordPolicy: PasswordPolicy = defaultPasswordPolicy
) {
    const { requires, minimumLength } = passwordPolicy
    const constraints = {
        atLeast8Chars: password.length >= minimumLength,
        atLeastOneUppercase: !requires.uppercase || /[A-Z]/.test(password),
        atLeastOneLowercase: !requires.lowercase || /[a-z]/.test(password),
        atLeastOneNumber: !requires.numbers || /\d/.test(password),
        atLeastOneSymbol: !requires.symbols || /[!@#$%^&*?~_]/.test(password),
    }
    return { ...constraints, isValid: Object.values(constraints).every(Boolean) }
}

/**
 * takes a password and validates it against a cognito password policy
 * fallback checks for lowercase, uppercase, special characters, and a length of at least 8
 * @param password string
 * @param passwordPolicy PasswordPolicyType
 * @returns boolean
 */
export function isPasswordValid(
    password: string,
    passwordPolicy: PasswordPolicy = defaultPasswordPolicy
) {
    const { requires, minimumLength } = passwordPolicy
    let valid = true

    if (requires.numbers) {
        const passed = /(?=.*\d)/g.test(password)
        if (!passed) {
            valid = false
        }
    }
    if (requires.lowercase) {
        const passed = /(?=.*[a-z])/g.test(password)
        if (!passed) {
            valid = false
        }
    }
    if (requires.uppercase) {
        const passed = /(?=.*[A-Z])/.test(password)
        if (!passed) {
            valid = false
        }
    }
    if (requires.symbols) {
        const passed = /(?=.*[!@#$%^&*?~_])/.test(password)
        if (!passed) {
            valid = false
        }
    }

    return valid && password.length >= minimumLength
}

/**
 * turns a cognito password policy into a requirement string
 * @param passwordPolicy: PasswordPolicyType
 * @returns string
 */
export function passwordPolicyToRequiresString(passwordPolicy: PasswordPolicyType | undefined) {
    if (!passwordPolicy) return defaultPasswordString

    const { requires } = passwordPolicy
    const requireStrings = []

    if (requires.numbers) {
        requireStrings.push("a number")
    }
    if (requires.lowercase) {
        requireStrings.push("a lowercase letter")
    }
    if (requires.uppercase) {
        requireStrings.push("an uppercase letter")
    }
    if (requires.symbols) {
        requireStrings.push("an uppercase letter")
    }

    return requireStrings.join(", ").replace(/, ([^,]*)$/, ", and $1")
}
